import React, { useContext, useEffect, useState } from "react";
import { Store } from "../../models/Store";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { ProblemDetails } from "../../models/Http";
import { UserContext } from "../../UserProvider";
import { hasRole, UserLocation } from "../../models/User";
import { permissions } from "../../constants";
import Alert from "@material-ui/lab/Alert";
import { AlertTitle } from "@material-ui/lab";
import { StoreSelect } from "../../components/selects/StoreSelect";
import {
  StoreStocktakingId,
  StoreStocktakingItem
} from "../../models/Stocktaking";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  Fab,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  TextField,
  useMediaQuery
} from "@material-ui/core";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../store";
import { isOk, Result } from "../../result";
import {
  Category,
  CategoryId,
  ProductMinimalModel
} from "../../models/Product";
import SaveIcon from "@material-ui/icons/Save";
import { CategoryMultiSelect } from "../../components/selects/CategoryMultiSelect";
import InfiniteScroll from "react-infinite-scroll-component";
import { ConfirmDialog } from "../../components/dialogs/ConfirmDialog";
import SearchIcon from "@material-ui/icons/Search";
import Fuse from "fuse.js";
import { thunkFetchAwaitingStocktakingItemsByStore } from "../../store/stocktaking/actions";
import * as StocktakingApi from "../../api/stocktakingApi";
import { fullscreen, Scanner } from "../../components/Scanner";
import CloseIcon from "@material-ui/icons/Close";
import { useParams } from "react-router-dom";
import { useI18n } from "../../hooks/useI18n";

// @ts-ignore
import { globalSettings } from "../../models/Settings";
import { ScanditBarcodeScanner } from "../../components/ScanditBarcodeScanner";

const useStyles = makeStyles(theme => ({
  loading: {
    marginTop: theme.spacing(10),
    display: "flex",
    justifyContent: "center",
    alignContent: "center"
  },
  section: {
    margin: theme.spacing(2)
  },
  sectionHeader: {
    marginBottom: theme.spacing(2)
  },
  searchField: {
    display: "flex",
    justifyContent: "center",
    marginBottom: theme.spacing(2)
  },
  storeSelect: {
    display: "flex",
    justifyContent: "center",
    margin: theme.spacing(2)
  },
  field: {
    display: "flex",
    justifyContent: "center"
  },
  divider: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  buttons: {
    margin: theme.spacing(2),
    position: "fixed",
    bottom: 0,
    right: 0
  },
  quitScanning: {
    position: "absolute",
    zIndex: 1000,
    top: theme.spacing(10),
    right: theme.spacing(4),
    display: "flex",
    justifyContent: "center",
    backgroundColor: "#fff",
    padding: theme.spacing(1)
  },
  scanner: {
    "& > div": {
      flex: 1
    }
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500]
  }
}));

interface SelectStore {
  type: "SELECT_STORE";
}
interface LoadItems {
  type: "LOAD_ITEMS";
  storeId: any;
}
interface Loading {
  type: "LOADING";
  storeId: any;
  filterCategoryIds: ReadonlyArray<CategoryId>;
  searchPhrase?: string;
}
interface Loaded {
  type: "LOADED";
  storeId: any;
  all: ReadonlyArray<StoreStocktakingItem>;
  filtered: ReadonlyArray<StoreStocktakingItem>;
  showing: ReadonlyArray<StoreStocktakingItem>;
  page: number;
  pageSize: number;
  filterCategoryIds: ReadonlyArray<CategoryId>;
  searchPhrase?: string;
}
interface Scanning {
  type: "SCANNING";
  storeId: any;
  all: ReadonlyArray<StoreStocktakingItem>;
  filtered: ReadonlyArray<StoreStocktakingItem>;
  showing: ReadonlyArray<StoreStocktakingItem>;
  page: number;
  pageSize: number;
  filterCategoryIds: ReadonlyArray<CategoryId>;
}
interface ShowConfirmDialog {
  type: "CONFIRM_DIALOG";
  storeId: any;
  all: ReadonlyArray<StoreStocktakingItem>;
  filtered: ReadonlyArray<StoreStocktakingItem>;
  showing: ReadonlyArray<StoreStocktakingItem>;
  page: number;
  pageSize: number;
  filterCategoryIds: ReadonlyArray<CategoryId>;
  searchPhrase?: string;
}
interface ReportItems {
  type: "REPORT_ITEMS";
  storeId: any;
  items: ReadonlyArray<StoreStocktakingItem>;
  filterCategoryIds: ReadonlyArray<CategoryId>;
  searchPhrase?: string;
}
interface NotAllowed {
  type: "NOT_ALLOWED";
}
export interface UnexpectedError {
  type: "UNEXPECTED_ERROR";
  problem: ProblemDetails;
}
type State =
  | SelectStore
  | LoadItems
  | Loading
  | Loaded
  | Scanning
  | ShowConfirmDialog
  | ReportItems
  | NotAllowed
  | UnexpectedError;

export const AwaitingStoreStocktaking = () => {
  const classes = useStyles();
  const params = useParams();
  const user = useContext(UserContext);
  const dispatch = useDispatch();
  const { t } = useI18n();

  const theme = useTheme();

  const mobile = useMediaQuery(theme.breakpoints.down("xs"));

  const [showScanner, setShowScanner] = useState<boolean>(false);

  const [state, setState] = useState<State>(
    user.type === "ANONYMOUS" || !hasRole(user, permissions.stores)
      ? { type: "NOT_ALLOWED" }
      : params.storeId === "any"
      ? { type: "SELECT_STORE" }
      : { type: "LOAD_ITEMS", storeId: params.storeId }
  );

  const awaitingStoreStocktakingItems = useSelector<
    AppState,
    Result<ProblemDetails, ReadonlyArray<StoreStocktakingItem>>
  >(s => s.awaitingStoreStocktakingItems);
  const renderSecondaryText = (product: ProductMinimalModel): string => {
    let rendered = "";

    if (product.brandName) {
      rendered += product.brandName;
    }

    if (product.size) {
      if (rendered) {
        rendered += ", ";
      }

      rendered += product.size;
    }

    if (rendered) {
      rendered += ", ";
    }

    rendered += product.gtin;

    return rendered;
  };

  const handleStoreSelected = (store: Store) => {
    return setState({ type: "LOAD_ITEMS", storeId: store.id });
  };

  const handleFilterSelected = (categories: ReadonlyArray<Category>) => {
    if (state.type !== "LOADED") {
      return;
    }

    const categoryIds = categories.map(c => c.id);

    const items = getFilteredItems(
      state.all,
      state.searchPhrase ?? "",
      categoryIds,
      false
    );

    return setState({
      ...state,
      type: "LOADED",
      page: 0,
      filterCategoryIds: categoryIds,
      filtered: items,
      showing: items.splice(0, state.pageSize)
    });
  };

  const handleSearch = (searchPhrase: string) => {
    if (state.type !== "LOADED" && state.type !== "SCANNING") {
      return;
    }

    const items = getFilteredItems(
      state.all,
      searchPhrase,
      state.filterCategoryIds,
      state.type === "SCANNING"
    );

    return setState({
      ...state,
      type: "LOADED",
      page: 0,
      searchPhrase: searchPhrase,
      filtered: items,
      showing: items.splice(0, state.pageSize)
    });
  };

  const handleOnShelfChange = (id: StoreStocktakingId, newValue?: number) => {
    if (state.type !== "LOADED") {
      return;
    }

    const index = state.all.findIndex(x => x.id === id);
    state.all[index].onShelf = newValue;
    state.all[index].onShelfChanged = newValue !== undefined;
  };

  const handleFetchMore = () => {
    if (state.type !== "LOADED") {
      return;
    }

    const currentPage = state.page;
    const nextPage = currentPage + 1;
    const currentItems = state.showing;
    const nextItems = state.filtered.slice(
      nextPage * state.pageSize,
      nextPage * state.pageSize + state.pageSize > state.filtered.length
        ? state.filtered.length
        : nextPage * state.pageSize + state.pageSize
    );
    const mergedItems = currentItems.concat(nextItems);

    setState({
      ...state,
      page: nextPage,
      showing: mergedItems
    });
  };

  const handleSave = (): boolean => {
    if (state.type !== "CONFIRM_DIALOG") {
      return true;
    }

    const items = getModifiedItems();

    if (items.length > 0) {
      setState({ ...state, type: "REPORT_ITEMS", items: items });
    } else {
      setState({ ...state, type: "LOADED" });
    }

    return true;
  };

  const getModifiedItems = (): ReadonlyArray<StoreStocktakingItem> => {
    if (state.type !== "CONFIRM_DIALOG") {
      return [];
    }

    return state.all.filter(i => i.onShelfChanged || i.inStorageChanged);
  };

  const getFilteredItems = (
    items: ReadonlyArray<StoreStocktakingItem>,
    searchPhrase: string,
    filterCategoryIds: ReadonlyArray<CategoryId>,
    fromScanner: boolean
  ): Array<StoreStocktakingItem> => {
    const filters = new Set(filterCategoryIds);
    const filtered = items.filter(
      item =>
        filterCategoryIds.length === 0 || filters.has(item.product.category.id)
    );

    if (fromScanner) {
      const exactItem = filtered.find(x => x.product.gtin === searchPhrase);
      return exactItem ? [exactItem] : [];
    }

    const searchOptions: Fuse.FuseOptions<StoreStocktakingItem> = {
      keys: ["searchMetaData"]
    };

    let fused: any =
      searchPhrase !== ""
        ? new Fuse(filtered, searchOptions).search(searchPhrase)
        : new Fuse(filtered, searchOptions);

    fused = fused.list ? fused.list : fused;

    return fused;
  };

  const loadAndFilterItems = (
    storeId: string,
    items: ReadonlyArray<StoreStocktakingItem>,
    filterCategoryIds: ReadonlyArray<string>,
    searchPhrase: string
  ) => {
    const all = items.map(item => ({
      ...item,
      searchMetaData: `${item.product.gtin}, ${item.product.brandName}, ${item.product.name}, ${item.product.category.name}`
    }));
    const filtered = getFilteredItems(
      all,
      searchPhrase,
      filterCategoryIds,
      false
    );

    return setState({
      type: "LOADED",
      storeId: storeId,
      all: all,
      filtered: filtered,
      showing: filtered.slice(0, 10),
      page: 0,
      pageSize: 10,
      filterCategoryIds: filterCategoryIds,
      searchPhrase: searchPhrase
    });
  };

  useEffect(() => {
    if (params.storeId && params.storeId !== "any")
      setState({ ...state, type: "LOAD_ITEMS", storeId: params.storeId });
  }, [params]);

  useEffect(() => {
    switch (state.type) {
      case "LOAD_ITEMS":
        dispatch(thunkFetchAwaitingStocktakingItemsByStore(state.storeId));
        setState({
          type: "LOADING",
          storeId: state.storeId,
          filterCategoryIds: [],
          searchPhrase: ""
        });
        break;

      case "REPORT_ITEMS":
        setState({ ...state, type: "LOADING" });

        StocktakingApi.reportByStore(state.storeId, state.items).then(
          result => {
            if (!isOk(result)) {
              return setState({
                type: "UNEXPECTED_ERROR",
                problem: result.details
              });
            }

            return loadAndFilterItems(
              state.storeId,
              result.value,
              state.filterCategoryIds,
              state.searchPhrase ?? ""
            );
          }
        );

        break;
    }
  }, [state.type]);

  useEffect(() => {
    if (state.type !== "LOADING") {
      return;
    }

    if (!isOk(awaitingStoreStocktakingItems)) {
      return setState({
        type: "UNEXPECTED_ERROR",
        problem: awaitingStoreStocktakingItems.details
      });
    }

    return loadAndFilterItems(
      state.storeId,
      awaitingStoreStocktakingItems.value,
      state.filterCategoryIds,
      state.searchPhrase ?? ""
    );
  }, [awaitingStoreStocktakingItems]);

  switch (state.type) {
    case "UNEXPECTED_ERROR":
      return (
        <Alert severity="error">
          <AlertTitle>
            {state.problem.status}, {state.problem.title}
          </AlertTitle>
          {state.problem.detail} <strong>{state.problem.traceId}</strong>
        </Alert>
      );

    case "NOT_ALLOWED":
      return (
        <Alert severity="warning">
          {t("common.messages.notAllowedToSeeThisView")}
        </Alert>
      );

    case "SELECT_STORE":
      return (
        <>
          <Alert severity="info">
            Start by selecting which store you are in.
          </Alert>
          <StoreSelect
            className={classes.storeSelect}
            filterOpened={true}
            onSelected={handleStoreSelected}
          />
        </>
      );

    case "LOAD_ITEMS":
    case "REPORT_ITEMS":
    case "LOADING":
      return (
        <div className={classes.loading}>
          <CircularProgress />
        </div>
      );

    case "CONFIRM_DIALOG":
    case "LOADED":
      return (
        <>
          <div className={classes.section}>
            <TextField
              id="search-field"
              className={classes.searchField}
              value={state.searchPhrase}
              placeholder={"Input search phrase"}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Button onClick={() => setShowScanner(true)}>Scan</Button>
                  </InputAdornment>
                )
              }}
              onChange={e => handleSearch(e.target.value.toString())}
            />
            <CategoryMultiSelect
              className={classes.field}
              label={t("common.formFields.categoryFilter")}
              value={state.filterCategoryIds}
              onSelected={handleFilterSelected}
            />
          </div>
          <List>
            <InfiniteScroll
              dataLength={state.showing.length}
              next={handleFetchMore}
              hasMore={
                state.filtered.length > state.pageSize * (state.page + 1)
              }
              loader={<ListItem key="loading">Loading...</ListItem>}
            >
              {state.showing && state.showing.length === 0 ? (
                <ListItem key="not_found">
                  <Grid item xs={12}>
                    <Divider className={classes.divider} />
                    <ListItemText primary={"No items found..."} />
                  </Grid>
                </ListItem>
              ) : null}

              {state.showing &&
                state.showing.length > 0 &&
                state.showing.map(item => (
                  <ListItem key={item.id}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Divider className={classes.divider} />
                        <ListItemText
                          primary={item.product.name}
                          secondary={renderSecondaryText(item.product)}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          className={classes.field}
                          label={t("common.formFields.onShelf")}
                          type="number"
                          defaultValue={item.onShelf}
                          disabled={
                            !item.onShelfChanged && item.onShelf !== undefined
                          }
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">st</InputAdornment>
                            )
                          }}
                          onChange={e =>
                            handleOnShelfChange(
                              item.id,
                              e.target.value === ""
                                ? undefined
                                : Number(e.target.value)
                            )
                          }
                        />
                      </Grid>
                    </Grid>
                  </ListItem>
                ))}
            </InfiniteScroll>
          </List>
          <div className={classes.buttons}>
            <Fab
              color="primary"
              aria-label="save"
              onClick={() => setState({ ...state, type: "CONFIRM_DIALOG" })}
            >
              <SaveIcon />
            </Fab>
          </div>
          <ConfirmDialog
            visible={state.type === "CONFIRM_DIALOG"}
            titleText={"Are you sure?"}
            bodyText={`Do you want to save the changes on ${
              getModifiedItems().length
            } product(s)?`}
            confirmText="Yes"
            cancelText="No"
            onConfirm={handleSave}
            onCancel={() => setState({ ...state, type: "LOADED" })}
          />
          <Dialog
            fullWidth={true}
            fullScreen={mobile}
            maxWidth={"sm"}
            open={showScanner}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <MuiDialogTitle disableTypography>
              <IconButton
                aria-label="close"
                className={classes.closeButton}
                onClick={() => {
                  setShowScanner(false);
                }}
              >
                <CloseIcon />
              </IconButton>
            </MuiDialogTitle>
            <DialogContent
              style={
                mobile
                  ? { padding: 0, marginTop: 15 }
                  : { marginTop: 15, marginBottom: 30 }
              }
            >
              <div
                className={classes.scanner}
                style={mobile ? { display: "flex", height: "70%" } : {}}
              >
                {showScanner && (
                  <ScanditBarcodeScanner
                    licenseKey={
                      globalSettings.settings.scandit.scannerLicenseKey
                    }
                    libraryLocation="https://cdn.jsdelivr.net/npm/@scandit/web-datacapture-barcode@8.0.0/sdc-lib/"
                    onScan={(result: any) => {
                      const { barcodes } = result;
                      if (barcodes.length > 0) {
                        const barcodeData = barcodes[0].data;

                        handleSearch(barcodeData);
                        // setState({ ...state, searchPhrase: barcodeData });
                        setShowScanner(false);
                      }
                    }}
                  />
                )}
              </div>
            </DialogContent>
          </Dialog>
        </>
      );
    default:
      return <></>;
  }
};
